import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Mail, Send } from 'lucide-react';

const EmailSenderDialog = ({ 
  open, 
  onOpenChange, 
  onSend, 
  documentType = "document",
  recipientEmail,
  documentInfo = {},
  showRibOption = true  // Allow RIB selection for devis, factures, notes d'auteur (not for contrats)
}) => {
  const [senderEmails, setSenderEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [ribs, setRibs] = useState([]);
  const [includeRib, setIncludeRib] = useState(false);
  const [selectedRibId, setSelectedRibId] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      loadEmails();
      if (showRibOption) {
        loadRibs();
      }
    }
  }, [open, showRibOption]);

  const loadEmails = async () => {
    try {
      const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
      const emails = res.data.sender_emails || [];
      const defaultEmail = res.data.default_sender_email || emails[0];
      
      setSenderEmails(emails);
      setSelectedEmail(defaultEmail);
    } catch (error) {
      console.error('Error loading sender emails:', error);
      // Fallback to default if error
      setSenderEmails(['kolnoaprod@gmail.com']);
      setSelectedEmail('kolnoaprod@gmail.com');
    } finally {
      setLoading(false);
    }
  };

  const loadRibs = async () => {
    try {
      const res = await axios.get(`${API}/ribs`, { withCredentials: true });
      setRibs(res.data || []);
      
      // Auto-select default RIB
      const defaultRib = res.data.find(r => r.is_default);
      if (defaultRib) {
        setSelectedRibId(defaultRib.id);
      } else if (res.data.length > 0) {
        setSelectedRibId(res.data[0].id);
      }
    } catch (error) {
      console.error('Error loading RIBs:', error);
    }
  };

  const handleSend = async () => {
    if (includeRib && !selectedRibId) {
      alert('Veuillez sélectionner un RIB');
      return;
    }

    setSending(true);
    try {
      // Pass RIB info to the parent handler
      await onSend(selectedEmail, includeRib ? selectedRibId : null);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-600" />
            Envoyer {documentType}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Recipient info */}
          <div className="p-3 bg-stone-50 rounded-lg">
            <Label className="text-xs text-stone-600">Destinataire</Label>
            <p className="text-sm font-medium font-mono">{recipientEmail || 'Email non disponible'}</p>
            {documentInfo.numero && (
              <p className="text-xs text-stone-500 mt-1">
                {documentType} N° {documentInfo.numero}
              </p>
            )}
          </div>
          
          {/* Sender email selector */}
          <div>
            <Label htmlFor="sender_email">Envoyer depuis</Label>
            <Select 
              value={selectedEmail} 
              onValueChange={setSelectedEmail} 
              disabled={loading || sending}
            >
              <SelectTrigger id="sender_email">
                <SelectValue placeholder="Sélectionner un email" />
              </SelectTrigger>
              <SelectContent>
                {senderEmails.map((email) => (
                  <SelectItem key={email} value={email}>
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3 text-stone-400" />
                      {email}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-500 mt-1">
              Vous pouvez gérer vos adresses email dans Paramètres Entreprise
            </p>
          </div>

          {/* RIB Selection - Only for certain document types */}
          {showRibOption && ribs.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  id="include_rib"
                  checked={includeRib}
                  onChange={(e) => setIncludeRib(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="include_rib" className="cursor-pointer font-medium">
                  Inclure le RIB en pied de page du PDF
                </Label>
              </div>

              {includeRib && (
                <div>
                  <Label htmlFor="rib_select">Sélectionner le RIB</Label>
                  <Select value={selectedRibId} onValueChange={setSelectedRibId}>
                    <SelectTrigger id="rib_select">
                      <SelectValue placeholder="Choisir un RIB" />
                    </SelectTrigger>
                    <SelectContent>
                      {ribs.map((rib) => (
                        <SelectItem key={rib.id} value={rib.id}>
                          {rib.label} {rib.is_default && '(Par défaut)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">
                    Le RIB sélectionné sera visible en pied de page du PDF
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={!selectedEmail || !recipientEmail || sending}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Envoi en cours...' : 'Envoyer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSenderDialog;
